import { Link } from "react-router-dom";
import { Stamp, SectionLabel, Headline } from "@/components/Doc";
import { FolderCard } from "@/components/FolderCard";
import { PaperCover, PaperGate, PAPER } from "@/pages/Paper";
import { MODULES } from "@/data/modules";

const artifacts = [
  { name: "The Verified Contact Register", caption: "Every counterparty, with a number you obtained yourselves.", stamp: "VERIFIED", r: -6 },
  { name: "The Duty Log", caption: "What was done, when, on whose instruction. Bound and numbered.", stamp: "LOGGED", r: 4 },
  { name: "The Authority Matrix", caption: "Who decides, and who decides when they're gone.", stamp: "AUTHORISED", r: -3 },
  { name: "The Continuity Binder", caption: "The physical binder for the worst morning of the year.", stamp: "RESTRICTED", r: 6 },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      <section className="relative min-h-[calc(100vh-4rem)] flex items-center overflow-hidden">
        <div className="absolute top-10 right-5 sm:right-16 lg:right-28">
          <Stamp color="red" rotate={7} size="lg" testId="hero-stamp">Restricted</Stamp>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 w-full">
          <p className="t-label text-olive" data-testid="hero-eyebrow">Longhand — Form LH-01 — Rev. 09/26</p>
          <h1 className="t-head mt-8 text-[3.2rem] leading-[0.92] sm:text-7xl lg:text-[7.5rem] max-w-5xl" data-testid="hero-headline">
            <span className="carbon-copy" data-text="One login opens everything.">One login opens everything.</span>
          </h1>
          <p className="t-body mt-10 max-w-[60ch] text-lg sm:text-xl leading-[1.65]" data-testid="hero-subhead">
            Your email account can reset your banking password, which can drain your operating account. Your CRM, your payroll, your vendor records, your books — one credential, one person, one bad morning. Most companies didn't decide to work this way. They just stopped noticing.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/audit" className="btn btn-stamp" data-testid="hero-audit-btn">Take the Key Ring Audit</Link>
            <Link to="/modules" className="btn" data-testid="hero-modules-btn">See the Four Modules</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="diagnosis-section">
        <SectionLabel right="Form LH-02">Doc. 02 — The Diagnosis</SectionLabel>
        <div className="grid lg:grid-cols-12 gap-12 mt-12">
          <div className="lg:col-span-5">
            <Headline className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">The threat isn't the technology. It's the consolidation.</Headline>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 t-body text-lg sm:text-xl leading-[1.65] flex flex-col gap-6">
            <p>Companies collapsed a dozen separate trust boundaries into a single sign-on, and then artificial intelligence made it cheap to convincingly impersonate a voice, a face, or a writing style well enough to get through that one door. In 2024 an employee at the engineering firm Arup joined a video call with people he believed were his colleagues and authorised transfers reported at roughly twenty-five million dollars. Every other participant on that call was synthetic.</p>
            <p>No firewall prevents that. A rule requiring two signatures on any wire — a rule that was standard practice in 1952 — prevents it completely.</p>
            <p>We don't sell software. We reinstall the operating controls your company deleted on its way to being digital, and we do it in a room, with your people, in two days.</p>
          </div>
        </div>
      </section>

      <section className="border-t border-rule bg-manila/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="paper-section">
          <SectionLabel right={PAPER.form}>Doc. 02A — The Paper</SectionLabel>
          <div className="grid lg:grid-cols-12 gap-12 mt-12 items-start">
            <div className="lg:col-span-5">
              <Headline className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Twenty-two pages on how the locks got collapsed.</Headline>
              <p className="t-body text-lg sm:text-xl mt-8 leading-[1.65] max-w-[50ch]">Six cases read closely, seven controls that predate the computer, and a ninety-day install with nothing to buy in week one. The firm's white paper, released against a name and a work email.</p>
              <blockquote className="t-body text-xl italic mt-8 pl-6 border-l-2 border-carbon max-w-[46ch] leading-snug">“{PAPER.opening}”</blockquote>
              <Link to="/paper" className="t-label inline-block mt-8 text-carbon hover:text-stamp" data-testid="home-paper-link">Read the contents →</Link>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex flex-col gap-8">
              <PaperCover compact />
              <PaperGate compact />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-rule">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="curriculum-section">
          <SectionLabel right="Form LH-03">Doc. 03 — The Curriculum</SectionLabel>
          <Headline className="mt-12 text-4xl sm:text-5xl lg:text-6xl">The four modules.</Headline>
          <div className="grid md:grid-cols-2 gap-10 mt-14">
            {MODULES.map((m) => <FolderCard key={m.slug} m={m} />)}
          </div>
        </div>
      </section>

      <section className="border-t border-rule">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="leave-behind-section">
          <SectionLabel right="Form LH-04">Doc. 04 — The Leave-Behind</SectionLabel>
          <div className="grid lg:grid-cols-12 gap-12 mt-12">
            <div className="lg:col-span-6">
              <Headline className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">A training day that changes nothing on Monday was entertainment.</Headline>
            </div>
            <p className="lg:col-span-5 lg:col-start-8 t-body text-lg sm:text-xl leading-[1.65]">
              Every Longhand module ends two ways. A physical object your team can hold, sign, and hang on a wall. And a written change to how the work is actually done — a rule, a threshold, a signature, a standing review. We write both into a closing memo, and we come back in ninety days to see whether they held.
            </p>
          </div>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {artifacts.map((a, i) => (
              <div key={a.name} className="relative bg-paper border border-carbon p-6 pt-14 min-h-[220px] shadow-[4px_4px_0_0_#D9C9A8]" data-testid={`artifact-card-${i}`}>
                <div className="absolute top-4 right-4"><Stamp color="carbon" rotate={a.r} size="sm">{a.stamp}</Stamp></div>
                <span className="t-label text-rule">Exhibit {String(i + 1).padStart(2, "0")}</span>
                <h3 className="t-head text-2xl mt-2 leading-none">{a.name}</h3>
                <p className="t-body text-base mt-3 text-olive">{a.caption}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-carbon text-paper relative overflow-hidden" data-testid="audit-cta-section">
        <div className="absolute top-8 right-6 sm:right-20"><Stamp color="paper" rotate={-6} size="lg">Received</Stamp></div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <span className="t-label text-manila">Doc. 05 — Form LH-90</span>
          <h2 className="t-head mt-8 text-4xl sm:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">How many doors does your email open?</h2>
          <p className="t-body mt-8 max-w-[60ch] text-lg sm:text-xl leading-[1.65] text-manila">
            Ten questions. Four minutes. You'll receive a one-page findings report showing what a single compromised credential reaches inside your company, and which of the four modules addresses it.
          </p>
          <Link to="/audit" className="btn btn-stamp mt-12" data-testid="cta-begin-audit-btn">Begin the Audit</Link>
        </div>
      </section>
    </main>
  );
}
